import { EllipsisText } from "@components/ellipsis-text";
import { Modal } from "@components/modal";
import { RowTable } from "@components/row-table";
import { StatusBlock, StatusBlockType } from "@components/status-block";
import { Config, DoubleText } from "@components/table";
import {
  PaymentCredited,
  PaymentNotCredited,
} from "@features/appeals/components/status";
import { MergedAppeal } from "@features/appeals/slice";
import { formatDate, formatTime } from "@helpers/date";
import { formatRubles, formatUsdt } from "@helpers/format";
import React from "react";

type Props = {
  isOpen: boolean;
  hide: () => void;
  data: MergedAppeal;
};

const config: Config<MergedAppeal> = [
  {
    label: "Номер заявки",
    source: "order_uuid",
    renderComponent: (data) => (
      <EllipsisText text={data} copy startLength={10} />
    ),
  },
  {
    label: "Invid заявки",
    source: "order_invid",
    renderComponent: (data) => (
      <EllipsisText text={data} copy startLength={10} />
    ),
  },
  {
    label: "Тип",
    source: "order_type",
    renderComponent: (order_type) => {
      switch (order_type) {
        case "pay_out":
          return "Вывод";
        case "pay_in":
          return "Сделка";
        default:
          return "Не определено";
      }
    },
  },
  {
    label: "Касса",
    source: "cash_register",
    renderComponent: (cell) => cell.name,
  },
  {
    label: "Дата и время заявки",
    source: "order_created_at",
    renderComponent: (dateTime) => {
      const time = formatTime(new Date(dateTime));
      const date = formatDate(new Date(dateTime));
      return <DoubleText text={time} subText={date} />;
    },
  },
  {
    label: "Дата и время апелляции",
    source: "created_at",
    renderComponent: (dateTime) => {
      const time = formatTime(new Date(dateTime));
      const date = formatDate(new Date(dateTime));
      return <DoubleText text={time} subText={date} />;
    },
  },
  {
    label: "Сумма заявки",
    source: "order_start_amount",
    renderComponent: (cell, item) => {
      if (cell == null || item.order_start_amount_usdt == null) return "";
      const rub = formatRubles(cell);
      const usdt = formatUsdt(item.order_start_amount_usdt);
      return <DoubleText text={rub} subText={usdt} />;
    },
  },
  {
    label: "Измененная сумма",
    source: "merchant_amount",
    renderComponent: (cell, item) => {
      if (cell == null || item.merchant_amount_usdt == null) return "";
      const rub = formatRubles(cell);
      const usdt = formatUsdt(item.merchant_amount_usdt);
      return <DoubleText text={rub} subText={usdt} />;
    },
    visible: (_, item) => item.order_type == "pay_in",
  },
  {
    label: "Итоговая сумма",
    source: "order_final_amount",
    renderComponent: (cell, item) => {
      if (cell == null || item.order_final_amount_usdt == null) return "";
      const rub = formatRubles(cell);
      const usdt = formatUsdt(item.order_final_amount_usdt);
      return <DoubleText text={rub} subText={usdt} />;
    },
    visible: (_, item) => item.order_type == "pay_in",
  },
  {
    source: "exchange_rate",
    label: "Курс",
    renderComponent: (data) => formatUsdt(data, true),
  },
  {
    source: "check_image_links",
    label: "Файл",
    renderComponent: (data) =>
      data &&
      data.map((file) => (
        <React.Fragment key={file}>
          <a className="underline" href={file} target="_blank">
            {file.split("/").pop()}
          </a>
          <br />
        </React.Fragment>
      )),
  },
  {
    source: "message",
    label: "Ваше сообщение",
  },
  {
    label: "Статус заявки",
    source: "order_status",
    renderComponent: (status) => {
      let text = "";
      let statusType: StatusBlockType;
      switch (status) {
        case "canceled":
          text = "Отклонена";
          statusType = StatusBlockType.Error;
          break;
        case "completed":
          text = "Успешна";
          statusType = StatusBlockType.Success;
          break;
        case "dispute_open":
          text = "Открыт спор";
          statusType = StatusBlockType.Warning;
          break;
        case "pending":
        case "processing":
        default:
          text = "В процессе";
          statusType = StatusBlockType.Warning;
          break;
      }
      return <StatusBlock text={text} type={statusType} />;
    },
  },
  // {
  //   label: "Статус",
  //   source: "status",
  //   renderComponent: (status) => {
  //     let text = "";
  //     let statusType: StatusBlockType;
  //     switch (status) {
  //       case "canceled":
  //         text = "Отклонен";
  //         statusType = StatusBlockType.Error;
  //         break;
  //       case "approved":
  //         text = "Пересмотрен";
  //         statusType = StatusBlockType.Success;
  //         break;
  //       case "processing":
  //       case "pending_support":
  //       case "created":
  //       default:
  //         text = "На рассмотрении";
  //         statusType = StatusBlockType.Warning;
  //         break;
  //     }
  //     return <StatusBlock text={text} type={statusType} />;
  //   },
  // },
  { label: "Реквизит", source: "card_number" },
  {
    label: "ID клиента мерчанта",
    source: "card_number",
    renderComponent: () => "",
  },
  { label: "Банк", source: "bank" },
  {
    label: "Решение от платформы",
    source: "platform_approve",
    visible: (item) => item != null,
    renderComponent: (item) => {
      if (item == null) return;

      return item ? "Подтверждена" : "Отклонена";
    },
  },
  {
    label: "Время принятия решения платформой",
    source: "platform_decided_at",
    visible: (item) => item != null,
    renderComponent: (dateTime) => {
      if (!dateTime) return "";
      const time = formatTime(new Date(dateTime));
      const date = formatDate(new Date(dateTime));
      return <DoubleText text={time} subText={date} />;
    },
  },
];

export const MerchantAppealModal = ({ isOpen, hide, data }: Props) => {
  return (
    <Modal isOpen={isOpen} onClose={hide} title={`Апелляция №${data.id}`}>
      {data.status === "approved" && (
        <PaymentCredited text="Аппеляция принята" />
      )}

      {data.status === "canceled" && (
        <PaymentNotCredited text="Аппеляция отменена" />
      )}
      <RowTable config={config} data={data} />
    </Modal>
  );
};
